import type {
  Rollout,
  Targeting,
  TargetingOperator,
  TargetingRule,
} from "../../../api/platform.service.js";

// Limits mirror platform-api's TargetingSchema.
export const MAX_RULES = 20;
export const MAX_VALUES_PER_RULE = 100;
export const MAX_VALUE_LENGTH = 200;
export const MAX_ATTRIBUTE_LENGTH = 64;
export const ATTRIBUTE_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** A rule being edited. `id` is client-only and never sent to the API. */
export type TargetingRuleDraft = TargetingRule & { id: string };

export type RuleFieldErrors = {
  attribute?: string;
  values?: string;
  variantKey?: string;
};

export type TargetingValidation = {
  /** Same order and length as the rules passed in. */
  ruleErrors: RuleFieldErrors[];
  formErrors: {
    rules?: string;
    fallback?: string;
  };
  isValid: boolean;
};

export const OPERATOR_LABELS: Record<TargetingOperator, string> = {
  in: "is one of",
  not_in: "is not one of",
};

let nextRuleId = 0;

export function newRuleId(): string {
  nextRuleId += 1;
  return `rule-${nextRuleId}`;
}

export function createRuleDraft(variantKeys: string[]): TargetingRuleDraft {
  return {
    id: newRuleId(),
    attribute: "",
    operator: "in",
    values: [],
    variantKey: variantKeys[0] ?? "",
  };
}

export function toRuleDrafts(targeting: Targeting | null | undefined): TargetingRuleDraft[] {
  if (!targeting) return [];
  return targeting.rules.map(rule => ({ ...rule, values: [...rule.values], id: newRuleId() }));
}

function validateAttribute(attribute: string): string | undefined {
  if (attribute.length === 0) return "Enter an attribute";
  if (attribute.length > MAX_ATTRIBUTE_LENGTH) {
    return `Attribute must be ${MAX_ATTRIBUTE_LENGTH} characters or fewer`;
  }
  if (!ATTRIBUTE_PATTERN.test(attribute)) {
    return "Attribute must start with a letter and contain only letters, numbers and underscores";
  }
  return undefined;
}

function validateValues(values: string[]): string | undefined {
  if (values.length === 0) return "Add at least one value";
  if (values.length > MAX_VALUES_PER_RULE) {
    return `A rule can have at most ${MAX_VALUES_PER_RULE} values`;
  }
  if (values.some(value => value.length === 0)) return "Values cannot be empty";
  if (values.some(value => value.length > MAX_VALUE_LENGTH)) {
    return `Each value must be ${MAX_VALUE_LENGTH} characters or fewer`;
  }
  return undefined;
}

export function validateTargeting(
  rules: TargetingRule[],
  fallbackVariantKey: string | null,
  variantKeys: string[],
): TargetingValidation {
  const declared = new Set(variantKeys);
  const ruleErrors = rules.map(rule => {
    const errors: RuleFieldErrors = {};
    const attribute = validateAttribute(rule.attribute);
    if (attribute) errors.attribute = attribute;
    const values = validateValues(rule.values);
    if (values) errors.values = values;
    if (!declared.has(rule.variantKey)) errors.variantKey = "Choose a declared variant";
    return errors;
  });

  const formErrors: TargetingValidation["formErrors"] = {};
  if (rules.length > MAX_RULES) {
    formErrors.rules = `A flag can have at most ${MAX_RULES} rules`;
  }
  // The fallback is dropped from the payload when there are no rules, so only check it then.
  if (rules.length > 0 && fallbackVariantKey !== null && !declared.has(fallbackVariantKey)) {
    formErrors.fallback = "Otherwise serve must be a declared variant";
  }

  const isValid =
    ruleErrors.every(errors => Object.keys(errors).length === 0) &&
    Object.keys(formErrors).length === 0;

  return { ruleErrors, formErrors, isValid };
}

/** Splits "a, b,,c" into ["a", "b", "c"]. */
export function parseValueInput(text: string): string[] {
  return text
    .split(",")
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

/** Appends values that aren't already in the list, keeping order. */
export function mergeValues(existing: string[], added: string[]): string[] {
  const result = [...existing];
  for (const value of added) {
    if (!result.includes(value)) result.push(value);
  }
  return result;
}

/** The request attribute that carries the Redis slot name. */
export const SLOT_ATTRIBUTE = "slot";

/**
 * Whether any rule targets the slot attribute. Slot names change on every blue/green switch,
 * so these rules need a warning. Matches the exact name only, like evaluation does.
 */
export function hasSlotRule(rules: Pick<TargetingRule, "attribute">[]): boolean {
  return rules.some(rule => rule.attribute === SLOT_ATTRIBUTE);
}

export function moveRule<T>(rules: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (index < 0 || index >= rules.length || target < 0 || target >= rules.length) return rules;
  const next = [...rules];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function toTargetingPayload(
  rules: TargetingRuleDraft[],
  fallbackVariantKey: string | null,
): Targeting | null {
  if (rules.length === 0) return null;
  return {
    rules: rules.map(rule => ({
      attribute: rule.attribute,
      operator: rule.operator,
      values: [...rule.values],
      variantKey: rule.variantKey,
    })),
    fallbackVariantKey,
  };
}

export function targetingSummary(targeting: Targeting | null | undefined): string | null {
  const count = targeting?.rules.length ?? 0;
  if (count === 0) return null;
  return count === 1 ? "1 rule" : `${count} rules`;
}

function rolloutSplit(rollout: Rollout): string {
  return rollout.weightedVariants.map(v => `${v.variantKey}: ${v.weight}%`).join(", ");
}

/**
 * Plain-language lines describing how a request is evaluated, in order.
 * `rollout` is what the form would save; `variantKeys` are the declared variants.
 */
export function describeTargeting(
  rules: TargetingRule[],
  fallbackVariantKey: string | null,
  rollout: Rollout | null,
  variantKeys: string[],
): string[] {
  const lines = rules.map((rule, index) => {
    const prefix = index === 0 ? "If" : "Else if";
    const attribute = rule.attribute || "(attribute)";
    const values = rule.values.length > 0 ? rule.values.join(", ") : "(no values)";
    const variant = rule.variantKey || "(variant)";
    return `${prefix} ${attribute} ${OPERATOR_LABELS[rule.operator]} ${values} → ${variant}.`;
  });

  if (fallbackVariantKey !== null) {
    lines.push(`Otherwise → ${fallbackVariantKey}.`);
  } else if (rollout) {
    lines.push(`Otherwise → percentage rollout (${rolloutSplit(rollout)}).`);
  } else if (variantKeys.length > 0) {
    lines.push(`Otherwise → ${variantKeys[0]} (first declared variant).`);
  } else {
    lines.push("Otherwise → default variant.");
  }
  return lines;
}

/** Short one-line form for the history table. `null` means no targeting. */
export function targetingToText(targeting: Targeting | null | undefined): string | null {
  const summary = targetingSummary(targeting);
  if (!targeting || !summary) return null;
  const rules = targeting.rules
    .map(
      rule => `${rule.attribute} ${rule.operator} ${rule.values.join(", ")} → ${rule.variantKey}`,
    )
    .join("; ");
  const otherwise = targeting.fallbackVariantKey ?? "rollout/default";
  return `targeting: ${summary} (${rules}), otherwise ${otherwise}`;
}
